// OrphanCleaningsTab — pestaña superadmin para detectar y limpiar
// aseos huérfanos: aquellos cuyo `reserva_id` apunta a una reserva
// que ya no existe en la tabla `reservas`.
//
// Por qué existe (round 2026-05-29): durante el periodo del cron de
// Stays paralizado (17-may a 29-may 2026) se acumularon ~30 aseos
// huérfanos por la combinación de:
//   1. Falta de FK física en `aseos.reserva_id`.
//   2. Delete HTTP de booking sin transacción.
//   3. `cleanupBookingDependencies` que tragaba errores.
// Los 3 agujeros se cerraron (FK ON DELETE SET NULL + tx HTTP + summary
// con errores). Esta pestaña es la 5ª capa: vista admin para limpiar
// los huérfanos existentes y operar manualmente si el cron de
// auditoría dispara una alerta futura.
//
// IMPORTANTE — relación con la regla "conservar aseo" del menú:
//   - Cuando el admin elige "Eliminar solo arriendo (conservar aseo)",
//     el código del backend pone `reserva_id = NULL`. El aseo queda
//     como evento independiente. **Eso NO es un huérfano**.
//   - Un huérfano es un aseo con `reserva_id = 1338` (apuntando a un
//     id que no existe). Es un estado ilegal, no la opción del admin.
//   - El botón "Aplicar limpieza" desvincula (pone reserva_id=NULL),
//     NO borra el aseo. Aplica el mismo tratamiento que "conservar
//     aseo" como red de seguridad. Para borrar definitivamente, hay
//     que ir a /aseos y borrar a mano.
//
// Ver docs/calendario-cascada-arriendos.md §"Red de seguridad: FK +
// auditoría de huérfanos".

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Tag,
  Typography,
  Statistic,
  Row,
  Col,
  Divider,
  DatePicker,
} from "antd";
import type { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import {
  SearchOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  DeleteOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { apiFetch } from "../../services/apiClient";
import { getModal, getMsg } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { ahoraChile } from "../../utils/fechaChile";
import { paginacionCliente } from "../../utils/tablaPaginacion";

const { Text, Paragraph, Title } = Typography;

interface OrphanRow {
  id: number;
  id_propiedad: number;
  nombrepropiedad: string | null;
  fecha_inicio: string | null;
  reserva_id: number;
  estado: string | null;
  categoria: string | null;
}

interface LegacyOrphanRow {
  id: number;
  id_propiedad: number;
  nombrepropiedad: string | null;
  fecha_inicio: string | null;
  estado: string | null;
  categoria: string | null;
}

interface ScanResponse {
  scanned: number;
  orphans: OrphanRow[];
}

interface LegacyScanResponse {
  scanned: number;
  orphans: LegacyOrphanRow[];
}

interface CleanupResponse {
  applied: boolean;
  scanned: number;
  affected: number;
  orphans: OrphanRow[];
}

interface LegacyCleanupResponse {
  applied: boolean;
  scanned: number;
  affected: number;
  orphans: LegacyOrphanRow[];
}

// Reservas sin aseo AUTO — Regla 2 violada (round 2026-05-29 fase 2).
// Síntoma del deadlock app-level del lavado durante el cron paralizado.
interface MissingAseoRow {
  id: number;
  id_propiedad: number;
  nombrepropiedad: string | null;
  ingreso: string | null;
  salida: string | null;
  nombre_huesped: string | null;
  nombre_plataforma: string | null;
}

interface MissingScanResponse {
  scanned: number;
  missing: MissingAseoRow[];
}

interface BackfillResponse {
  applied: boolean;
  scanned: number;
  created: number;
  failed: number;
  missing: MissingAseoRow[];
  failures: Array<{ bookingId: number; error: string }>;
}

// Bloque 4 — aseos cuyo id_propiedad apunta a OTRA propiedad que la
// del booking vinculado (round 2026-05-30).
interface MismatchedRow {
  aseo_id: number;
  aseo_fecha: string | null;
  aseo_prop_id: number;
  aseo_prop_nombre: string | null;
  booking_id: number;
  booking_prop_id: number;
  booking_prop_nombre: string | null;
  nombre_huesped: string | null;
}

interface MismatchedScanResponse {
  scanned: number;
  mismatched: MismatchedRow[];
}

interface MismatchedFixResponse {
  applied: boolean;
  scanned: number;
  affected: number;
  mismatched: MismatchedRow[];
}

const columns: ColumnsType<OrphanRow> = [
  {
    title: "Aseo ID",
    dataIndex: "id",
    key: "id",
    width: 90,
    sorter: (a, b) => a.id - b.id,
  },
  {
    title: "Propiedad",
    dataIndex: "nombrepropiedad",
    key: "nombrepropiedad",
    ellipsis: true,
    render: (v: string | null) =>
      v ? formatPropertyName(v) : <Text type="secondary">—</Text>,
  },
  {
    title: "Fecha aseo",
    dataIndex: "fecha_inicio",
    key: "fecha_inicio",
    width: 120,
    sorter: (a, b) =>
      (a.fecha_inicio ?? "").localeCompare(b.fecha_inicio ?? ""),
  },
  {
    title: "Apunta a reserva",
    dataIndex: "reserva_id",
    key: "reserva_id",
    width: 150,
    render: (v: number) => (
      <Tag color="red" icon={<WarningOutlined />}>
        #{v} (no existe)
      </Tag>
    ),
  },
  {
    title: "Estado",
    dataIndex: "estado",
    key: "estado",
    width: 120,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
  {
    title: "Categoría",
    dataIndex: "categoria",
    key: "categoria",
    width: 110,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
];

// Columnas para la tabla de reservas SIN aseo AUTO. Mostramos la
// info mínima del booking para que el admin reconozca el caso a
// simple vista (huésped + propiedad + fechas).
const missingColumns: ColumnsType<MissingAseoRow> = [
  {
    title: "Reserva ID",
    dataIndex: "id",
    key: "id",
    width: 100,
    sorter: (a, b) => a.id - b.id,
  },
  {
    title: "Propiedad",
    dataIndex: "nombrepropiedad",
    key: "nombrepropiedad",
    ellipsis: true,
    render: (v: string | null) =>
      v ? formatPropertyName(v) : <Text type="secondary">—</Text>,
  },
  {
    title: "Huésped",
    dataIndex: "nombre_huesped",
    key: "nombre_huesped",
    ellipsis: true,
    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
  },
  {
    title: "Ingreso",
    dataIndex: "ingreso",
    key: "ingreso",
    width: 110,
    sorter: (a, b) => (a.ingreso ?? "").localeCompare(b.ingreso ?? ""),
  },
  {
    title: "Salida",
    dataIndex: "salida",
    key: "salida",
    width: 110,
    sorter: (a, b) => (a.salida ?? "").localeCompare(b.salida ?? ""),
  },
  {
    title: "Plataforma",
    dataIndex: "nombre_plataforma",
    key: "nombre_plataforma",
    width: 110,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
];

// Columnas de la tabla "aseos mal ubicados" (round 2026-05-30).
// Mostramos las dos propiedades (la actual incorrecta y la correcta)
// lado a lado para que el admin entienda visualmente el cambio que se
// va a aplicar.
const mismatchedColumns: ColumnsType<MismatchedRow> = [
  {
    title: "Aseo ID",
    dataIndex: "aseo_id",
    key: "aseo_id",
    width: 90,
    sorter: (a, b) => a.aseo_id - b.aseo_id,
  },
  {
    title: "Fecha",
    dataIndex: "aseo_fecha",
    key: "aseo_fecha",
    width: 110,
    sorter: (a, b) =>
      (a.aseo_fecha ?? "").localeCompare(b.aseo_fecha ?? ""),
  },
  {
    title: "Huésped",
    dataIndex: "nombre_huesped",
    key: "nombre_huesped",
    ellipsis: true,
    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
  },
  {
    title: "Ahora en (INCORRECTO)",
    dataIndex: "aseo_prop_nombre",
    key: "aseo_prop_nombre",
    ellipsis: true,
    render: (v: string | null) =>
      v ? (
        <Tag color="red" icon={<WarningOutlined />}>
          {formatPropertyName(v)}
        </Tag>
      ) : (
        <Text type="secondary">—</Text>
      ),
  },
  {
    title: "Debería estar en",
    dataIndex: "booking_prop_nombre",
    key: "booking_prop_nombre",
    ellipsis: true,
    render: (v: string | null) =>
      v ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          {formatPropertyName(v)}
        </Tag>
      ) : (
        <Text type="secondary">—</Text>
      ),
  },
];

// Columnas de la tabla de huérfanos legacy. Comparten estructura con
// las de FK rota pero sin la columna `reserva_id` (acá siempre es NULL,
// no aporta info).
const legacyColumns: ColumnsType<LegacyOrphanRow> = [
  {
    title: "Aseo ID",
    dataIndex: "id",
    key: "id",
    width: 90,
    sorter: (a, b) => a.id - b.id,
  },
  {
    title: "Propiedad",
    dataIndex: "nombrepropiedad",
    key: "nombrepropiedad",
    ellipsis: true,
    render: (v: string | null) =>
      v ? formatPropertyName(v) : <Text type="secondary">—</Text>,
  },
  {
    title: "Fecha aseo",
    dataIndex: "fecha_inicio",
    key: "fecha_inicio",
    width: 120,
    sorter: (a, b) =>
      (a.fecha_inicio ?? "").localeCompare(b.fecha_inicio ?? ""),
  },
  {
    title: "Estado",
    dataIndex: "estado",
    key: "estado",
    width: 120,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
  {
    title: "Categoría",
    dataIndex: "categoria",
    key: "categoria",
    width: 110,
    render: (v: string | null) =>
      v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
  },
];

const OrphanCleaningsTab = () => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scanned, setScanned] = useState<number | null>(null);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);

  // Estado independiente para el bloque legacy (huérfanos del
  // bootstrap a4e23cc). Mismo patrón scan + apply pero con DELETE.
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyApplying, setLegacyApplying] = useState(false);
  const [legacyScanned, setLegacyScanned] = useState<number | null>(null);
  const [legacyOrphans, setLegacyOrphans] = useState<LegacyOrphanRow[]>([]);

  // Bloque 3 — reservas SIN aseo AUTO (Regla 2 violada). La acción acá
  // es CREATE (corre runBookingCascade por cada reserva), no UPDATE
  // ni DELETE. Idempotente.
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingApplying, setMissingApplying] = useState(false);
  const [missingScanned, setMissingScanned] = useState<number | null>(null);
  const [missing, setMissing] = useState<MissingAseoRow[]>([]);
  // Rango por defecto: último año hasta dentro de 1 año. Cubre todo el
  // operativo sin tocar histórico muy viejo. El admin puede ampliarlo.
  const [missingRange, setMissingRange] = useState<[Dayjs, Dayjs] | null>([
    ahoraChile().subtract(1, "year"),
    ahoraChile().add(1, "year"),
  ]);

  // Bloque 4 — aseos mal ubicados (id_propiedad != booking.id_propiedad).
  const [mismatchedLoading, setMismatchedLoading] = useState(false);
  const [mismatchedApplying, setMismatchedApplying] = useState(false);
  const [mismatchedScanned, setMismatchedScanned] = useState<number | null>(
    null,
  );
  const [mismatched, setMismatched] = useState<MismatchedRow[]>([]);

  const runScan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ success: boolean; data: ScanResponse }>(
        "/admin/cleanup-orphan-cleanings",
      );
      if (res?.data) {
        setScanned(res.data.scanned);
        setOrphans(res.data.orphans ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const runLegacyScan = useCallback(async () => {
    setLegacyLoading(true);
    try {
      const res = await apiFetch<{
        success: boolean;
        data: LegacyScanResponse;
      }>("/admin/cleanup-legacy-orphan-cleanings");
      if (res?.data) {
        setLegacyScanned(res.data.scanned);
        setLegacyOrphans(res.data.orphans ?? []);
      }
    } finally {
      setLegacyLoading(false);
    }
  }, []);

  const runMismatchedScan = useCallback(async () => {
    setMismatchedLoading(true);
    try {
      const res = await apiFetch<{
        success: boolean;
        data: MismatchedScanResponse;
      }>("/admin/mismatched-aseo-properties");
      if (res?.data) {
        setMismatchedScanned(res.data.scanned);
        setMismatched(res.data.mismatched ?? []);
      }
    } finally {
      setMismatchedLoading(false);
    }
  }, []);

  const runMissingScan = useCallback(async () => {
    setMissingLoading(true);
    try {
      // Construimos la query string con el rango si está seteado.
      // Sin rango → el backend usa su default (último año + futuro).
      const params = new URLSearchParams();
      if (missingRange?.[0]) params.set("from", missingRange[0].format("YYYY-MM-DD"));
      if (missingRange?.[1]) params.set("to", missingRange[1].format("YYYY-MM-DD"));
      const qs = params.toString();
      const url = qs ? `/admin/missing-aseos?${qs}` : "/admin/missing-aseos";
      const res = await apiFetch<{
        success: boolean;
        data: MissingScanResponse;
      }>(url);
      if (res?.data) {
        setMissingScanned(res.data.scanned);
        setMissing(res.data.missing ?? []);
      }
    } finally {
      setMissingLoading(false);
    }
  }, [missingRange]);

  // Scan automático al abrir el tab — es ligero y permite ver el
  // estado de un vistazo sin tener que clickear "Buscar". Si hay 0
  // huérfanos, se ve la tarjeta verde inmediatamente. Lanzamos los
  // tres escaneos en paralelo (FK rota + legacy + reservas sin aseo)
  // porque son independientes y baratos.
  useEffect(() => {
    void runScan();
    void runLegacyScan();
    void runMissingScan();
    void runMismatchedScan();
  }, [runScan, runLegacyScan, runMissingScan, runMismatchedScan]);

  const handleApply = useCallback(() => {
    if (orphans.length === 0) {
      getMsg()?.info("No hay huérfanos para limpiar.");
      return;
    }
    getModal().confirm({
      title: `¿Desvincular ${orphans.length} aseos huérfanos?`,
      icon: <ToolOutlined style={{ color: "#fa8c16" }} />,
      content: (
        <div>
          <Paragraph>
            Se va a setear <Text code>reserva_id = NULL</Text> en{" "}
            <strong>{orphans.length}</strong> aseos. El efecto es el mismo
            que "Eliminar solo arriendo (conservar aseo)" del menú del
            calendario: los aseos quedan como eventos independientes, NO
            se borran.
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Para borrarlos definitivamente, ve a <Text code>/aseos</Text>{" "}
            uno por uno después.
          </Paragraph>
        </div>
      ),
      okText: "Desvincular",
      okType: "primary",
      okButtonProps: { danger: false },
      cancelText: "Cancelar",
      onOk: async () => {
        setApplying(true);
        try {
          const res = await apiFetch<{
            success: boolean;
            data: CleanupResponse;
          }>("/admin/cleanup-orphan-cleanings", {
            method: "POST",
            body: JSON.stringify({ apply: true }),
          });
          if (res?.data) {
            getMsg()?.success(
              `${res.data.affected} aseos desvinculados de su reserva.`,
            );
            // Re-scan para actualizar la vista.
            await runScan();
          }
        } finally {
          setApplying(false);
        }
      },
    });
  }, [orphans, runScan]);

  // Acción del bloque legacy. A diferencia del de arriba, este
  // BORRA (DELETE). El backend tiene doble filtro de seguridad
  // (lista de IDs + condición original en el WHERE), así que si un
  // aseo de la lista cambió de estado entre el scan y el delete,
  // no lo borra. Mensaje de confirmación lo deja claro.
  const handleLegacyApply = useCallback(() => {
    if (legacyOrphans.length === 0) {
      getMsg()?.info("No hay residuos legacy para borrar.");
      return;
    }
    getModal().confirm({
      title: `¿Borrar ${legacyOrphans.length} aseos huérfanos legacy?`,
      icon: <DeleteOutlined style={{ color: "#cf1322" }} />,
      content: (
        <div>
          <Paragraph>
            Se van a borrar <strong>{legacyOrphans.length}</strong> aseos
            del bootstrap del round <Text code>a4e23cc</Text>. Patrón:
            sin reserva, sin aseador, sin observación, en estado
            pendiente — residuos imposibles de recuperar porque la
            reserva original ya no existe.
          </Paragraph>
          <Paragraph type="warning" style={{ marginBottom: 0 }}>
            Esta acción es <strong>irreversible</strong>. Si quieres
            mirarlos antes, expande la tabla de abajo o filtra en{" "}
            <Text code>/aseos</Text> por "sin reserva".
          </Paragraph>
        </div>
      ),
      okText: "Borrar definitivamente",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setLegacyApplying(true);
        try {
          const res = await apiFetch<{
            success: boolean;
            data: LegacyCleanupResponse;
          }>("/admin/cleanup-legacy-orphan-cleanings", {
            method: "POST",
            body: JSON.stringify({ apply: true }),
          });
          if (res?.data) {
            getMsg()?.success(
              `${res.data.affected} aseos legacy eliminados.`,
            );
            await runLegacyScan();
          }
        } finally {
          setLegacyApplying(false);
        }
      },
    });
  }, [legacyOrphans, runLegacyScan]);

  // Apply para el bloque "reservas sin aseo": corre runBookingCascade
  // por cada una. NO es destructivo (solo crea aseos), pero igual
  // confirmamos con modal porque la operación es inusual y el admin
  // debería ver qué se va a hacer antes.
  const handleMissingApply = useCallback(() => {
    if (missing.length === 0) {
      getMsg()?.info("No hay reservas sin aseo para corregir.");
      return;
    }
    getModal().confirm({
      title: `¿Crear aseo AUTO para ${missing.length} reservas?`,
      icon: <SyncOutlined style={{ color: "#1677ff" }} />,
      content: (
        <div>
          <Paragraph>
            Se va a ejecutar <Text code>runBookingCascade</Text> sobre{" "}
            <strong>{missing.length}</strong> reservas que no tienen
            aseo AUTO vinculado. Cada cascada crea el aseo AUTO + el
            bloqueo AUTO (si hay tardía) + sincroniza lavado + recompute
            de urgencias vecinas.
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Idempotente — si una reserva ya tuviera un aseo entre el
            scan y el apply, no se duplica.
          </Paragraph>
        </div>
      ),
      okText: "Crear aseos",
      okType: "primary",
      cancelText: "Cancelar",
      onOk: async () => {
        setMissingApplying(true);
        try {
          const res = await apiFetch<{
            success: boolean;
            data: BackfillResponse;
          }>("/admin/missing-aseos", {
            method: "POST",
            body: JSON.stringify({
              apply: true,
              from: missingRange?.[0]?.format("YYYY-MM-DD"),
              to: missingRange?.[1]?.format("YYYY-MM-DD"),
            }),
          });
          if (res?.data) {
            const { created, failed } = res.data;
            if (failed > 0) {
              getMsg()?.warning(
                `${created} aseos creados, ${failed} fallaron — revisar logs.`,
              );
            } else {
              getMsg()?.success(`${created} aseos AUTO creados correctamente.`);
            }
            await runMissingScan();
          }
        } finally {
          setMissingApplying(false);
        }
      },
    });
  }, [missing, runMissingScan, missingRange]);

  const handleMismatchedApply = useCallback(() => {
    if (mismatched.length === 0) {
      getMsg()?.info("No hay aseos mal ubicados para corregir.");
      return;
    }
    getModal().confirm({
      title: `¿Mover ${mismatched.length} aseos a la propiedad correcta?`,
      icon: <SyncOutlined style={{ color: "#1677ff" }} />,
      content: (
        <div>
          <Paragraph>
            Se va a actualizar el <Text code>id_propiedad</Text> de{" "}
            <strong>{mismatched.length}</strong> aseos vinculados a su
            booking. La nueva propiedad sale de{" "}
            <Text code>reservas.id_propiedad</Text> — la fuente de verdad
            de "a qué propiedad pertenece este booking".
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Idempotente — si un aseo cambió entre el scan y el apply (por
            ejemplo, un webhook lo movió antes), el UPDATE no lo toca
            (filtra otra vez por <Text code>id_propiedad &lt;&gt; r.id_propiedad</Text>).
          </Paragraph>
        </div>
      ),
      okText: "Mover aseos",
      okType: "primary",
      cancelText: "Cancelar",
      onOk: async () => {
        setMismatchedApplying(true);
        try {
          const res = await apiFetch<{
            success: boolean;
            data: MismatchedFixResponse;
          }>("/admin/mismatched-aseo-properties", {
            method: "POST",
            body: JSON.stringify({ apply: true }),
          });
          if (res?.data) {
            getMsg()?.success(
              `${res.data.affected} aseos movidos a su propiedad correcta.`,
            );
            await runMismatchedScan();
          }
        } finally {
          setMismatchedApplying(false);
        }
      },
    });
  }, [mismatched, runMismatchedScan]);

  return (
    <div style={{ padding: 16 }}>
      <Alert
        type="info"
        showIcon
        message="¿Qué es un aseo huérfano?"
        description={
          <div>
            Un aseo cuyo <Text code>reserva_id</Text> apunta a una reserva
            que ya no existe en la base. <strong>NO es lo mismo</strong>{" "}
            que la opción "conservar aseo" del menú al borrar un arriendo
            (esa pone <Text code>reserva_id = NULL</Text> y es legítima).
            Los huérfanos son un estado ilegal causado por un fallo del
            cascade. Con la FK <Text code>ON DELETE SET NULL</Text>{" "}
            instalada en round 2026-05-29, no deberían aparecer nuevos.
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Statistic
              title="Huérfanos detectados"
              value={scanned ?? "—"}
              prefix={
                scanned === 0 ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : scanned !== null && scanned > 0 ? (
                  <WarningOutlined style={{ color: "#cf1322" }} />
                ) : null
              }
              valueStyle={
                scanned === 0
                  ? { color: "#3f8600" }
                  : scanned !== null && scanned > 0
                    ? { color: "#cf1322" }
                    : undefined
              }
            />
          </Col>
          <Col xs={24} sm={16}>
            <Space wrap>
              <Button
                icon={<SearchOutlined />}
                onClick={runScan}
                loading={loading}
              >
                Re-escanear
              </Button>
              <Button
                type="primary"
                icon={<ToolOutlined />}
                onClick={handleApply}
                loading={applying}
                disabled={orphans.length === 0}
              >
                Desvincular todos ({orphans.length})
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {scanned === 0 && (
        <Alert
          type="success"
          showIcon
          message="No hay huérfanos"
          description="Todos los aseos con reserva_id apuntan a reservas existentes. El sistema está limpio."
        />
      )}

      {scanned !== null && scanned > 0 && (
        <Table<OrphanRow>
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={orphans}
          loading={loading}
          pagination={paginacionCliente({ porDefecto: 25 })}
        />
      )}

      {/* ───────────────────────────────────────────────────────────
          Bloque 2 — Huérfanos legacy (residuos del bootstrap a4e23cc).
          Separado visualmente del bloque de FK rota porque la acción
          es distinta (DELETE en vez de UPDATE) y el patrón también.
          Ver comentario al inicio del archivo y el módulo del backend
          services/orphanCleanings.js para el contexto completo.
          ─────────────────────────────────────────────────────────── */}
      <Divider style={{ margin: "32px 0 24px" }} />
      <Title level={4} style={{ marginTop: 0 }}>
        Huérfanos legacy (residuos del bootstrap)
      </Title>

      <Alert
        type="warning"
        showIcon
        message="¿Qué son los huérfanos legacy?"
        description={
          <div>
            Aseos sin <Text code>reserva_id</Text>, sin aseador, sin
            observación y en estado pendiente. Quedaron después de que el
            bootstrap del round <Text code>a4e23cc</Text> desvinculara los
            ~30 aseos cuya reserva original había sido borrada por el bug
            del cron paralizado.{" "}
            <strong>
              El admin los estuvo borrando uno por uno desde /aseos
            </strong>{" "}
            hasta cansarse — este botón hace lo mismo en una sola pasada.
            A diferencia del bloque de arriba, acá la acción es{" "}
            <Text code>DELETE</Text> (no UPDATE) porque la reserva
            original ya no existe ni volverá.
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Statistic
              title="Huérfanos legacy detectados"
              value={legacyScanned ?? "—"}
              prefix={
                legacyScanned === 0 ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : legacyScanned !== null && legacyScanned > 0 ? (
                  <WarningOutlined style={{ color: "#fa8c16" }} />
                ) : null
              }
              valueStyle={
                legacyScanned === 0
                  ? { color: "#3f8600" }
                  : legacyScanned !== null && legacyScanned > 0
                    ? { color: "#fa8c16" }
                    : undefined
              }
            />
          </Col>
          <Col xs={24} sm={16}>
            <Space wrap>
              <Button
                icon={<SearchOutlined />}
                onClick={runLegacyScan}
                loading={legacyLoading}
              >
                Re-escanear
              </Button>
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                onClick={handleLegacyApply}
                loading={legacyApplying}
                disabled={legacyOrphans.length === 0}
              >
                Borrar todos ({legacyOrphans.length})
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {legacyScanned === 0 && (
        <Alert
          type="success"
          showIcon
          message="No hay residuos legacy"
          description="Todos los aseos sin reserva tienen aseador o observación — son manuales legítimos, no residuos del bootstrap."
        />
      )}

      {legacyScanned !== null && legacyScanned > 0 && (
        <Table<LegacyOrphanRow>
          rowKey="id"
          size="middle"
          columns={legacyColumns}
          dataSource={legacyOrphans}
          loading={legacyLoading}
          pagination={paginacionCliente({ porDefecto: 25 })}
        />
      )}

      {/* ───────────────────────────────────────────────────────────
          Bloque 3 — Reservas sin aseo AUTO (Regla 2 violada).
          Round 2026-05-29 segunda fase. Detecta el síntoma opuesto a
          los huérfanos: bookings cuyo aseo AUTO nunca se creó por
          fallo silencioso del cascade durante el cron paralizado.
          Acción: CREATE (corre runBookingCascade), no UPDATE/DELETE.
          ─────────────────────────────────────────────────────────── */}
      <Divider style={{ margin: "32px 0 24px" }} />
      <Title level={4} style={{ marginTop: 0 }}>
        Reservas sin aseo AUTO
      </Title>

      <Alert
        type="warning"
        showIcon
        message="¿Qué son las reservas sin aseo?"
        description={
          <div>
            Reservas activas (salida ≥ hoy-30d) que NO tienen ningún
            aseo con <Text code>reserva_id</Text> apuntándoles. Violación
            de la <strong>Regla 2</strong> del MD: "todo arriendo tiene
            un aseo AUTO, siempre". Síntoma típico del deadlock
            app-level del lavado durante el cron paralizado (round
            2026-05-29). El código nuevo (<Text code>skipLavadoSync</Text>)
            ya previene esto para reservas futuras; este bloque
            backfilea las víctimas históricas corriendo{" "}
            <Text code>runBookingCascade</Text> por cada una.
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Statistic
              title="Reservas sin aseo detectadas"
              value={missingScanned ?? "—"}
              prefix={
                missingScanned === 0 ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : missingScanned !== null && missingScanned > 0 ? (
                  <WarningOutlined style={{ color: "#fa8c16" }} />
                ) : null
              }
              valueStyle={
                missingScanned === 0
                  ? { color: "#3f8600" }
                  : missingScanned !== null && missingScanned > 0
                    ? { color: "#fa8c16" }
                    : undefined
              }
            />
          </Col>
          <Col xs={24} sm={16}>
            <Space wrap direction="vertical" style={{ width: "100%" }}>
              <Space wrap>
                <Text type="secondary" style={{ marginRight: 4 }}>
                  Rango de salida:
                </Text>
                <DatePicker.RangePicker
                  value={missingRange}
                  onChange={(dates) => {
                    // El RangePicker devuelve [Dayjs|null, Dayjs|null] o null.
                    // Solo aceptamos rangos válidos completos para mantener
                    // la query coherente; si el admin "limpia" volvemos al
                    // default del último año.
                    if (dates && dates[0] && dates[1]) {
                      setMissingRange([dates[0], dates[1]]);
                    } else {
                      setMissingRange([
                        ahoraChile().subtract(1, "year"),
                        ahoraChile().add(1, "year"),
                      ]);
                    }
                  }}
                  format="YYYY-MM-DD"
                  allowClear={false}
                />
              </Space>
              <Space wrap>
                <Button
                  icon={<SearchOutlined />}
                  onClick={runMissingScan}
                  loading={missingLoading}
                >
                  Re-escanear
                </Button>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={handleMissingApply}
                  loading={missingApplying}
                  disabled={missing.length === 0}
                >
                  Crear aseos faltantes ({missing.length})
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>
      </Card>

      {missingScanned === 0 && (
        <Alert
          type="success"
          showIcon
          message="No hay reservas sin aseo"
          description="Todas las reservas activas tienen su aseo AUTO vinculado. Regla 2 cumplida."
        />
      )}

      {missingScanned !== null && missingScanned > 0 && (
        <Table<MissingAseoRow>
          rowKey="id"
          size="middle"
          columns={missingColumns}
          dataSource={missing}
          loading={missingLoading}
          pagination={paginacionCliente({ porDefecto: 25 })}
        />
      )}

      {/* ───────────────────────────────────────────────────────────
          Bloque 4 — Aseos mal ubicados (id_propiedad apunta a otra
          propiedad que la del booking vinculado). Round 2026-05-30.
          Causa: el UPDATE de syncCleaningFromBooking no incluía
          id_propiedad (fixed commit 7a4f28d). Esta acción corrige
          los datos históricos.
          ─────────────────────────────────────────────────────────── */}
      <Divider style={{ margin: "32px 0 24px" }} />
      <Title level={4} style={{ marginTop: 0 }}>
        Aseos en propiedad equivocada
      </Title>

      <Alert
        type="warning"
        showIcon
        message="¿Qué son los aseos mal ubicados?"
        description={
          <div>
            Aseos cuyo <Text code>id_propiedad</Text> apunta a una
            propiedad distinta a la del booking al que están vinculados.
            Visualmente se ven como aseos "huérfanos" en propiedades
            random sin booking, mientras la propiedad real del booking
            aparece sin aseo. Bug histórico que se manifestó
            principalmente durante el cron paralizado (mayo 2026). El
            código nuevo (commit <Text code>7a4f28d</Text>) ya incluye
            <Text code>id_propiedad</Text> en el update de{" "}
            <Text code>syncCleaningFromBooking</Text>, así que no
            deberían aparecer nuevos. Este botón corrige los históricos.
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Statistic
              title="Aseos mal ubicados"
              value={mismatchedScanned ?? "—"}
              prefix={
                mismatchedScanned === 0 ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : mismatchedScanned !== null && mismatchedScanned > 0 ? (
                  <WarningOutlined style={{ color: "#fa8c16" }} />
                ) : null
              }
              valueStyle={
                mismatchedScanned === 0
                  ? { color: "#3f8600" }
                  : mismatchedScanned !== null && mismatchedScanned > 0
                    ? { color: "#fa8c16" }
                    : undefined
              }
            />
          </Col>
          <Col xs={24} sm={16}>
            <Space wrap>
              <Button
                icon={<SearchOutlined />}
                onClick={runMismatchedScan}
                loading={mismatchedLoading}
              >
                Re-escanear
              </Button>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleMismatchedApply}
                loading={mismatchedApplying}
                disabled={mismatched.length === 0}
              >
                Mover a propiedad correcta ({mismatched.length})
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {mismatchedScanned === 0 && (
        <Alert
          type="success"
          showIcon
          message="No hay aseos mal ubicados"
          description="Todos los aseos vinculados a una reserva están en la propiedad correcta."
        />
      )}

      {mismatchedScanned !== null && mismatchedScanned > 0 && (
        <Table<MismatchedRow>
          rowKey="aseo_id"
          size="middle"
          columns={mismatchedColumns}
          dataSource={mismatched}
          loading={mismatchedLoading}
          pagination={paginacionCliente({ porDefecto: 25 })}
        />
      )}
    </div>
  );
};

export default OrphanCleaningsTab;
